import React from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useIntl } from 'react-intl';
import dayjs from 'dayjs';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { PopoverPortal } from '@radix-ui/react-popover';

import {
    Form,
    FormControl,
    FormControlStack,
    FormDescription,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from 'src/registry/ui/Form';
import TextareaFormField from 'src/registry/components/forms/TextareaFormField';
import { Button } from 'src/registry/ui/Button';
import { Calendar } from 'src/registry/ui/Calendar';
import { Input } from 'src/registry/ui/Input';
import { Popover, PopoverContent, PopoverTrigger } from 'src/registry/ui/Popover';
import { cn } from 'src/utils';
import { ICON_MAP } from 'src/utils/constants';
import { generalMessages } from 'src/utils/messages';
import { ReferenceRateValueFormSchemaValues } from 'src/features/referenceRateValues/utils/formSchema';
import { referenceRateValuesMessages } from 'src/features/referenceRateValues/utils/messages';
import { REFERENCE_RATE_UNIT, ReferenceRate } from 'src/features/referenceRates/utils/types';
import { REFERENCE_RATE_UNIT_DECIMALS, REFERENCE_RATE_UNIT_MAP } from 'src/features/referenceRates/utils/constants';
import {
    formatReferenceRateInputValue,
    getReferenceRateValuePattern,
    toReferenceRateMinorUnits,
} from 'src/features/referenceRates/utils/units';

type ReferenceRateValueFormProps = {
    form: UseFormReturn<ReferenceRateValueFormSchemaValues>;
    onSubmit: (values: ReferenceRateValueFormSchemaValues) => void;
    /** The parent rate. Its unit decides how the typed decimal maps onto the stored integer. */
    referenceRate?: ReferenceRate;
};

function ReferenceRateValueForm({ onSubmit, form, referenceRate }: ReferenceRateValueFormProps) {
    const { formatMessage, formatDate } = useIntl();
    const [displayAmount, setDisplayAmount] = React.useState('');
    const [openValidFromCalendar, setOpenValidFromCalendar] = React.useState<boolean>(false);

    // Rates read before the backend gained `unit` are money rates.
    const unit = referenceRate ? (referenceRate.unit ?? REFERENCE_RATE_UNIT.CURRENCY) : undefined;
    const valuePattern = React.useMemo(() => getReferenceRateValuePattern(unit), [unit]);
    // Read during render so react-hook-form's formState proxy subscribes us to resets.
    const formDefaultValues = form.formState.defaultValues;

    /*
     * The API stores an integer in minor units of the rate's unit; the input shows the decimal. Syncing only on
     * form reset (or once the rate's unit is known) keeps the display from being rewritten while typing.
     */
    React.useEffect(() => {
        if (!unit) return;
        const storedAmount = form.getValues('amount');
        setDisplayAmount(storedAmount != null ? formatReferenceRateInputValue(storedAmount, unit) : '');
    }, [form, formDefaultValues, unit]);

    const amountLabel = unit
        ? `${formatMessage(referenceRateValuesMessages.amount)} (${
              unit === REFERENCE_RATE_UNIT.CURRENCY && referenceRate?.currency
                  ? referenceRate.currency
                  : formatMessage(REFERENCE_RATE_UNIT_MAP[unit]).toLowerCase()
          })`
        : formatMessage(referenceRateValuesMessages.amount);

    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)} variant="dialog">
            <FormField
                control={form.control}
                name="valid_from"
                render={({ field }) => {
                    const selectedDate = field.value ? new Date(field.value) : undefined;

                    return (
                        <FormItem variant="dialog">
                            <FormLabel variant="dialog" required>
                                {formatMessage(generalMessages.validFrom)}
                            </FormLabel>
                            <FormControlStack>
                                <FormControl>
                                    <Popover open={openValidFromCalendar} onOpenChange={setOpenValidFromCalendar}>
                                        <PopoverTrigger asChild>
                                            <Button
                                                id="valid_from"
                                                variant="outline"
                                                className="w-full justify-start text-base font-normal"
                                                onClick={() => setOpenValidFromCalendar(true)}
                                            >
                                                <FontAwesomeIcon
                                                    icon={ICON_MAP.CALENDAR}
                                                    className="text-primary-700 mr-1 size-4"
                                                />
                                                <span className={cn(!field.value && 'text-gray-500')}>
                                                    {field.value
                                                        ? formatDate(new Date(field.value))
                                                        : `${formatMessage(generalMessages.selectDate)}...`}
                                                </span>
                                            </Button>
                                        </PopoverTrigger>
                                        <PopoverPortal>
                                            <PopoverContent
                                                className="z-50 max-h-(--radix-popover-content-available-height) overflow-y-auto bg-white p-4 shadow-sm"
                                                align="end"
                                            >
                                                <Calendar
                                                    captionLayout="dropdown"
                                                    mode="single"
                                                    selected={selectedDate}
                                                    onSelect={(date) => {
                                                        if (date) {
                                                            field.onChange(dayjs(date).format('YYYY-MM-DD'));
                                                            setOpenValidFromCalendar(false);
                                                        }
                                                    }}
                                                />
                                            </PopoverContent>
                                        </PopoverPortal>
                                    </Popover>
                                </FormControl>
                                <FormMessage />
                            </FormControlStack>
                        </FormItem>
                    );
                }}
            />
            {/* There is deliberately no end date field - see the form schema for why. */}
            <FormDescription>{formatMessage(referenceRateValuesMessages.contiguityHint)}</FormDescription>
            <FormField
                control={form.control}
                name="amount"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog" required>
                            {amountLabel}
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <Input
                                    type="text"
                                    inputMode="decimal"
                                    placeholder={`${formatMessage(referenceRateValuesMessages.amount)}...`}
                                    value={displayAmount}
                                    // The scale depends on the rate's unit, so typing waits until it is known.
                                    disabled={!unit}
                                    onBlur={() => {
                                        if (!displayAmount || unit !== REFERENCE_RATE_UNIT.CURRENCY) return;

                                        const numeric = parseFloat(displayAmount);
                                        if (!isNaN(numeric)) {
                                            setDisplayAmount(numeric.toFixed(REFERENCE_RATE_UNIT_DECIMALS[unit]));
                                        }
                                    }}
                                    onChange={(e) => {
                                        const inputValue = e.target.value;

                                        if (!valuePattern.test(inputValue)) return;

                                        setDisplayAmount(inputValue);

                                        const minorUnits = toReferenceRateMinorUnits(inputValue, unit);
                                        if (minorUnits != null) {
                                            field.onChange(minorUnits);
                                        } else {
                                            field.onChange(undefined);
                                            setDisplayAmount('');
                                        }
                                    }}
                                />
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
            <TextareaFormField<ReferenceRateValueFormSchemaValues>
                form={form}
                name="source_note"
                required={false}
                label={formatMessage(referenceRateValuesMessages.sourceNote)}
            />
        </Form>
    );
}

export default ReferenceRateValueForm;
